import { useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router'
import { useSesion } from '../auth/useSesion'
import { ConmutadorTema } from '../tema/ConmutadorTema'
import { Campana } from './Campana'
import { BotonGuia } from '../guia/BotonGuia'
import { useGuia } from '../guia/useGuia'
import { IconoSalir, IconoDesplegar, IconoMas, IconoCerrar } from './iconos'
import { GRUPOS, DESTINOS, type Destino } from './navegacion'

/**
 * Estructura de la aplicacion: barra lateral fija en escritorio y barra
 * inferior de iconos en movil.
 *
 * Escritorio: el menu se agrupa en tres SECCIONES COLAPSABLES (Operación
 * diaria · Clientes y Leads · Sistema). Con once destinos en fila plana el
 * navbar se saturaba; agrupar da orden y "Sistema" arranca plegado porque se
 * usa poco.
 *
 * Movil: a 375 px once iconos no caben, asi que la barra inferior lleva solo los
 * mas usados (`movilFijo`) mas un boton "Mas" que abre una hoja con el resto.
 *
 * Sin emojis: los iconos vienen de `iconos.tsx`. Los colores salen de los tokens
 * de `index.css`, nunca de una escala de Tailwind directa.
 */

interface Props {
  children: ReactNode
}

const claseEnlaceLateral = (activo: boolean) =>
  [
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    activo
      ? 'bg-acento-suave text-acento-tinta'
      : 'text-tinta-2 hover:bg-elevado hover:text-tinta',
  ].join(' ')

const claseEnlaceMovil = (activo: boolean) =>
  [
    'flex min-h-14 flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] font-medium whitespace-nowrap transition-colors',
    activo ? 'text-acento-tinta' : 'text-tinta-apagada',
  ].join(' ')

/** Una seccion colapsable del menu lateral. */
function SeccionLateral({
  titulo,
  destinos,
  abierta,
  onAlternar,
}: {
  titulo: string
  destinos: Destino[]
  abierta: boolean
  onAlternar: () => void
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onAlternar}
        aria-expanded={abierta}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-tinta-apagada transition-colors hover:text-tinta-2"
      >
        {titulo}
        <IconoDesplegar
          className={['ml-auto size-4 transition-transform', abierta ? '' : '-rotate-90'].join(' ')}
        />
      </button>
      {abierta ? (
        <div className="mt-0.5 space-y-1">
          {destinos.map(({ ruta, etiqueta, Icono, IconoActivo }) => (
            <NavLink key={ruta} to={ruta} data-tour={`nav-${ruta}`} className={({ isActive }) => claseEnlaceLateral(isActive)}>
              {({ isActive }) => (
                <>
                  {isActive ? <IconoActivo className="size-5 shrink-0" /> : <Icono className="size-5 shrink-0" />}
                  <span>{etiqueta}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function AppShell({ children }: Props) {
  const { sesion, salir } = useSesion()
  const correo = sesion?.user?.email ?? ''

  // Estado abierto/cerrado por grupo (clave = título). Arranca con el valor por
  // defecto de cada grupo (Sistema plegado).
  const [abiertos, setAbiertos] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(GRUPOS.map((g) => [g.titulo, !g.colapsadoPorDefecto])),
  )
  const alternarGrupo = (titulo: string) =>
    setAbiertos((prev) => ({ ...prev, [titulo]: !prev[titulo] }))

  // Movil: 5 fijos en la barra; el resto en la hoja "Mas".
  const fijosMovil = DESTINOS.filter((d) => d.movilFijo)
  const restoMovil = DESTINOS.filter((d) => !d.movilFijo)
  const [masAbierto, setMasAbierto] = useState(false)

  // Conversaciones va a sangre completa (3 columnas tipo bandeja): sin la caja
  // `max-w-7xl` ni el padding que encajonan al resto de pantallas, y sin
  // scroll de página — el scroll vive dentro de cada columna.
  const { pathname } = useLocation()
  const anchoCompleto = pathname.startsWith('/conversaciones')

  // Monta el controlador de la guía (limpia residuos de driver.js al navegar).
  useGuia()

  return (
    <div className="min-h-screen bg-lienzo text-tinta">
      {/* Escritorio: lateral fija. Oculta por debajo de md. */}
      <aside className="hidden md:fixed md:inset-y-0 md:left-0 md:z-40 md:flex md:w-64 md:flex-col md:border-r md:border-borde md:bg-superficie">
        <div className="px-5 py-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-tinta-apagada">
            Tactica Empresarial
          </p>
          <p className="mt-1 text-lg font-semibold text-tinta">Servicios Digitales</p>
        </div>

        <nav data-tour="menu" aria-label="Navegacion principal" className="flex-1 space-y-3 overflow-y-auto px-3">
          {GRUPOS.map((grupo) => (
            <SeccionLateral
              key={grupo.titulo}
              titulo={grupo.titulo}
              destinos={grupo.destinos}
              abierta={abiertos[grupo.titulo] ?? true}
              onAlternar={() => alternarGrupo(grupo.titulo)}
            />
          ))}
        </nav>

        <div className="border-t border-borde px-5 py-4">
          <ConmutadorTema />
          <p className="mt-4 truncate text-sm text-tinta-2" title={correo}>
            {correo}
          </p>
          <button
            type="button"
            onClick={() => void salir()}
            className="mt-2 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado hover:text-tinta"
          >
            <IconoSalir className="size-5 shrink-0" />
            <span>Cerrar sesion</span>
          </button>
        </div>
      </aside>

      <div className="relative md:pl-64">
        <div className="flex items-center justify-between gap-3 px-4 pt-4 md:hidden">
          <p className="text-sm font-semibold text-tinta">Servicios Digitales</p>
          <div className="flex items-center gap-2">
            <BotonGuia soloIcono />
            <div data-tour="campana">
              <Campana />
            </div>
            <ConmutadorTema rotuloVisible={false} />
          </div>
        </div>

        {/* Escritorio: franja superior con la campana a la derecha. En las
            pantallas a sangre completa (Conversaciones) NO ocupa altura: se
            superpone en la esquina para no empujar la bandeja de 3 columnas
            (que es h-screen) hacia abajo. */}
        <div
          className={
            anchoCompleto
              ? 'pointer-events-none absolute right-4 top-3 z-30 hidden md:block'
              : 'hidden items-center justify-end px-4 pt-4 md:flex md:px-8'
          }
        >
          <div className={anchoCompleto ? 'pointer-events-auto flex items-center gap-2' : 'flex items-center gap-2'}>
            <BotonGuia />
            <div data-tour="campana">
              <Campana />
            </div>
          </div>
        </div>

        <main className={anchoCompleto ? '' : 'mx-auto max-w-7xl px-4 pb-24 pt-6 md:px-8 md:pb-10'}>
          {children}
        </main>
      </div>

      {/* Movil: barra inferior con 5 fijos + "Mas". La rejilla reparte el ancho
          exacto (6 columnas: 5 destinos + el boton Mas). */}
      <nav
        aria-label="Navegacion principal movil"
        style={{ gridTemplateColumns: `repeat(${fijosMovil.length + 1}, minmax(0, 1fr))` }}
        className="fixed inset-x-0 bottom-0 z-40 grid border-t border-borde bg-superficie pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {fijosMovil.map(({ ruta, etiquetaMovil, Icono, IconoActivo }) => (
          <NavLink key={ruta} to={ruta} className={({ isActive }) => claseEnlaceMovil(isActive)}>
            {({ isActive }) => (
              <>
                {isActive ? <IconoActivo className="size-6 shrink-0" /> : <Icono className="size-6 shrink-0" />}
                <span>{etiquetaMovil}</span>
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMasAbierto(true)}
          aria-label="Más secciones"
          className="flex min-h-14 flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] font-medium text-tinta-apagada transition-colors hover:text-tinta-2"
        >
          <IconoMas className="size-6 shrink-0" />
          <span>Más</span>
        </button>
      </nav>

      {/* Hoja "Mas" (movil): el resto de destinos que no caben en la barra. */}
      {masAbierto ? (
        <div className="fixed inset-0 z-50 flex items-end md:hidden" onMouseDown={(e) => { if (e.target === e.currentTarget) setMasAbierto(false) }}>
          <div aria-hidden className="absolute inset-0 bg-[rgba(3,9,17,0.6)]" />
          <div className="relative max-h-[70vh] w-full overflow-y-auto rounded-t-2xl border-t border-borde bg-superficie pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-between border-b border-borde px-5 py-3">
              <p className="text-sm font-semibold text-tinta">Más secciones</p>
              <button type="button" onClick={() => setMasAbierto(false)} aria-label="Cerrar" className="rounded-md p-1 text-tinta-2 hover:bg-elevado">
                <IconoCerrar className="size-5" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 p-4">
              {restoMovil.map(({ ruta, etiquetaMovil, Icono, IconoActivo }) => (
                <NavLink
                  key={ruta}
                  to={ruta}
                  onClick={() => setMasAbierto(false)}
                  className={({ isActive }) =>
                    [
                      'flex flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-3 text-xs font-medium transition-colors',
                      isActive ? 'border-acento bg-acento-suave text-acento-tinta' : 'border-borde text-tinta-2 hover:bg-elevado',
                    ].join(' ')
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive ? <IconoActivo className="size-6" /> : <Icono className="size-6" />}
                      <span className="text-center">{etiquetaMovil}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
